import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/authFetch';
import { ViewIcon, EditIcon, DeleteIcon, StatusIcon } from '../components/IconButtons';
import AdminSidebar from '../components/AdminSidebar';
import * as XLSX from 'xlsx';

const ManajemenSiswaPage = () => {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportGradesModal, setShowImportGradesModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importGradesFile, setImportGradesFile] = useState(null);
  const [importGradesData, setImportGradesData] = useState([]);
  const [importGradesLoading, setImportGradesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: true // Open by default since we're in a master data page
  });
  const [user, setUser] = useState(null);

  // Real student data from API
  const [students, setStudents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
                  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
        let apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/students?page=${currentPage}`;
        if (searchQuery && searchQuery.trim() !== '') {
          apiUrl += `&search=${encodeURIComponent(searchQuery)}`;
        }

        // ...rest of the file...
        // In your function:
        const response = await authFetch(apiUrl);
        if (!response.ok) throw new Error('Failed to fetch students');
        const data = await response.json();
        setStudents(data.data);
        setLastPage(data.last_page);
        setTotal(data.total);
      } catch (err) {
        setNotification({ show: true, message: 'Gagal memuat data siswa: ' + err.message, type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, [currentPage, searchQuery]);

  const [formData, setFormData] = useState({
    nis: '',
    name: '',
    email: '',
    class: '',
    angkatan: '',
    noUrut: '', // absenceNumber
    averagePoints: '', // average_points
    address: '',
    addressNote: '', // addressDesc
    phone: '', // handphone
    fatherName: '',
    motherName: '',
    maidenName: '',
    emergencyName: '', // emergencyContactName
    emergencyPhone: '', // emergencyContactPhone
    emergencyRelation: '', // emergencyContactRelationship
    avatarUrl: '',
    avatarFile: null
  });

  // No need to filter students on the frontend anymore, backend search is used.
  const filteredStudents = students;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    // Use FormData for all fields including avatar
    const formDataToSend = new FormData();
    formDataToSend.append('nis', formData.nis);
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('class', formData.class);
    formDataToSend.append('angkatan', formData.angkatan);
    formDataToSend.append('absenceNumber', formData.noUrut);
    formDataToSend.append('average_points', formData.averagePoints);
    formDataToSend.append('address', formData.address);
    formDataToSend.append('addressDesc', formData.addressNote);
    formDataToSend.append('handphone', formData.phone);
    formDataToSend.append('fatherName', formData.fatherName);
    formDataToSend.append('motherName', formData.motherName);
    formDataToSend.append('maidenName', formData.maidenName);
    formDataToSend.append('emergencyContactName', formData.emergencyName);
    formDataToSend.append('emergencyContactPhone', formData.emergencyPhone);
    formDataToSend.append('emergencyContactRelationship', formData.emergencyRelation);
    // If avatar file selected, append it
    if (formData.avatarFile) {
      formDataToSend.append('avatar', formData.avatarFile);
    }
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/students`;

      const response = await authFetch(apiUrl, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }
        console.error('API error:', errorData);

        // Handle validation errors specifically
        if (response.status === 422 && errorData.errors) {
          // Format validation errors nicely
          const errorMessages = [];
          for (const field in errorData.errors) {
            errorMessages.push(errorData.errors[field][0]);
          }
          setNotification({
            show: true,
            message: 'Validasi gagal: ' + errorMessages.join(', '),
            type: 'error'
          });
        } else {
          setNotification({
            show: true,
            message: 'Gagal menambah siswa: ' + (errorData.message || JSON.stringify(errorData)),
            type: 'error'
          });
        }
        return;
      }

      const newStudent = await response.json();

      // Show success notification
      setNotification({
        show: true,
        message: 'Siswa berhasil ditambahkan!',
        type: 'success'
      });

      setStudents(prev => [...prev, newStudent]);
      setFormData({
        nis: '',
        name: '',
        email: '',
        class: '',
        angkatan: '',
        noUrut: '',
        averagePoints: '',
        address: '',
        addressNote: '',
        phone: '',
        fatherName: '',
        motherName: '',
        maidenName: '',
        emergencyName: '',
        emergencyPhone: '',
        emergencyRelation: '',
        avatarUrl: '',
        avatarFile: null
      });
      setShowAddModal(false);
    } catch (err) {
      console.error('Network error:', err);
      setNotification({
        show: true,
        message: 'Terjadi kesalahan jaringan: ' + err.message,
        type: 'error'
      });
    }
  };

  const handleEditStudent = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;
    const formDataToSend = new FormData();
    formDataToSend.append('nis', formData.nis);
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('class', formData.class);
    formDataToSend.append('angkatan', formData.angkatan);
    formDataToSend.append('absenceNumber', formData.noUrut);
    formDataToSend.append('average_points', formData.averagePoints);
    formDataToSend.append('address', formData.address);
    formDataToSend.append('addressDesc', formData.addressNote);
    formDataToSend.append('handphone', formData.phone);
    formDataToSend.append('fatherName', formData.fatherName);
    formDataToSend.append('motherName', formData.motherName);
    formDataToSend.append('maidenName', formData.maidenName);
    formDataToSend.append('emergencyContactName', formData.emergencyName);
    formDataToSend.append('emergencyContactPhone', formData.emergencyPhone);
    formDataToSend.append('emergencyContactRelationship', formData.emergencyRelation);
    if (formData.avatarFile) {
      formDataToSend.append('avatar', formData.avatarFile);
    }
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/students/${selectedStudent.id}`;
      const response = await authFetch(apiUrl, {
        method: 'POST',
        headers: {
          'X-HTTP-Method-Override': 'PATCH'
        },
        body: formDataToSend
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try { errorData = JSON.parse(errorText); } catch (e) { errorData = { message: errorText }; }

        // Handle validation errors specifically
        if (response.status === 422 && errorData.errors) {
          // Format validation errors nicely
          const errorMessages = [];
          for (const field in errorData.errors) {
            errorMessages.push(errorData.errors[field][0]);
          }
          setNotification({
            show: true,
            message: 'Validasi gagal: ' + errorMessages.join(', '),
            type: 'error'
          });
        } else {
          setNotification({ show: true, message: 'Gagal mengedit siswa: ' + (errorData.message || JSON.stringify(errorData)), type: 'error' });
        }
        return;
      }
      const updatedStudent = await response.json();
      setNotification({ show: true, message: 'Siswa berhasil diperbarui!', type: 'success' });
      setStudents(prev => prev.map(student => student.id === selectedStudent.id ? updatedStudent : student));
      setShowEditModal(false);
    } catch (err) {
      setNotification({ show: true, message: 'Terjadi kesalahan jaringan: ' + err.message, type: 'error' });
    }
  };

  const handleDeleteStudent = async () => {
    if (!selectedStudent) return;
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8001/';
    const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/students/${selectedStudent.id}`;
    try {
      const response = await authFetch(apiUrl, { method: 'DELETE' });
      if (!response.ok) throw new Error('Gagal menghapus siswa.');
      setStudents(prev => prev.filter(student => student.id !== selectedStudent.id));
      setNotification({ show: true, message: 'Siswa berhasil dihapus!', type: 'success' });
    } catch (err) {
      setNotification({ show: true, message: err.message || 'Gagal menghapus siswa.', type: 'error' });
    }
    setShowDeleteModal(false);
  };

  const openEditModal = (student) => {
    setSelectedStudent(student);
    setFormData({
      nis: student.nis || '',
      name: student.name || '',
      email: student.email || '',
      class: student.class || '',
      angkatan: student.angkatan || '',
      noUrut: student.absenceNumber || '',
      averagePoints: student.average_points || '',
      address: student.address || '',
      addressNote: student.addressDesc || '',
      phone: student.handphone || '',
      fatherName: student.fatherName || '',
      motherName: student.motherName || '',
      maidenName: student.maidenName || '',
      emergencyName: student.emergencyContactName || '',
      emergencyPhone: student.emergencyContactPhone || '',
      emergencyRelation: student.emergencyContactRelationship || '',
      avatarUrl: student.avatarUrl || '',
      avatarFile: null
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (student) => {
    setSelectedStudent(student);
    setShowDeleteModal(true);
  };

  const handleFileUpload = (e) => {
    console.log('File upload triggered');
    console.log('Event:', e);
    console.log('Event target:', e.target);
    console.log('Files:', e.target.files);
    const file = e.target.files[0];
    console.log('Selected file:', file);
    
    if (file) {
      console.log('File details:', {
        name: file.name,
        type: file.type,
        size: file.size
      });
      
      setImportFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        console.log('FileReader onload triggered');
        try {
          console.log('Starting Excel processing...');
          const data = new Uint8Array(event.target.result);
          console.log('Data length:', data.length);
          
          const workbook = XLSX.read(data, { type: 'array' });
          console.log('Workbook loaded, sheets:', workbook.SheetNames);
          
          const sheetName = workbook.SheetNames[0];
          console.log('Using sheet:', sheetName);
          
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
          
          if (jsonData.length > 0) {
            console.log('First row columns:', Object.keys(jsonData[0]));
            console.log('First row data:', jsonData[0]);
          }

          // Map Excel columns to our format
          const mappedData = jsonData.map((row, index) => {
            // Helper function to safely get string value
            const safeString = (value) => {
              if (value === null || value === undefined) return '';
              return String(value).trim();
            };

            // Helper function to safely get number value
            const safeNumber = (value) => {
              if (value === null || value === undefined || value === '') return null;
              const num = parseInt(value);
              return isNaN(num) ? null : num;
            };

            // Dynamically find the email value to be robust against header whitespace/casing
            let emailValue = '';
            for (const key in row) {
                if (key.trim().toLowerCase() === 'email') {
                    emailValue = row[key];
                    break; 
                }
            }

            const nisRaw = row['NIS'] || row['nis'] || row['Nis'] || '';
            const nameRaw = row['Nama'] || row['nama'] || row['name'] || row['Name'] || '';
            const emailRaw = emailValue;
            const kelasRaw = row['Kelas'] || row['kelas'] || row['class'] || row['Class'] || '';
            const angkatanRaw = row['Angkatan'] || row['angkatan'] || '';
            const nomorUrutRaw = row['Nomor Urut'] || row['nomor_urut'] || row['absenceNumber'] || row['No Urut'] || '';
            const alamatRaw = row['Alamat Domisili'] || row['alamat_domisili'] || row['address'] || row['Alamat'] || '';
            const keteranganAlamatRaw = row['Keterangan Domisili'] || row['keterangan_domisili'] || row['addressDesc'] || '';
            const nomorHPRaw = row['Nomor HP'] || row['nomor_hp'] || row['handphone'] || row['No HP'] || row['HP'] || '';
            const namaAyahRaw = row['Nama Ayah'] || row['nama_ayah'] || row['fatherName'] || row['Ayah'] || '';
            const namaIbuRaw = row['Nama Ibu'] || row['nama_ibu'] || row['motherName'] || row['Ibu'] || '';
            const namaWaliRaw = row['Nama Wali'] || row['nama_wali'] || row['maidenName'] || row['Wali'] || '';
            const namaKontakDaruratRaw = row['Nama Kontak Darurat'] || row['nama_kontak_darurat'] || row['emergencyContactName'] || row['Kontak Darurat'] || '';
            const noHpKontakDaruratRaw = row['No Hp Kontak Darurat'] || row['no_hp_kontak_darurat'] || row['emergencyContactPhone'] || row['HP Darurat'] || '';
            const hubunganDenganSiswaRaw = row['Hubungan dengan siswa'] || row['hubungan_dengan_siswa'] || row['emergencyContactRelationship'] || row['Hubungan'] || '';

            // --- DEBUG: Log the raw and processed email for the first few rows ---
            if (index < 3) {
              console.log(`--- DEBUG: Processing Row ${index + 2} ---`);
              console.log(`Value of email column (raw):`, emailRaw);
              console.log(`Value of email column (processed with safeString):`, safeString(emailRaw));
            }

            const nis = safeNumber(nisRaw);
            const name = safeString(nameRaw);
            const email = safeString(emailRaw);
            const kelas = safeString(kelasRaw);
            const angkatan = safeString(angkatanRaw);
            const nomorUrut = safeNumber(nomorUrutRaw);
            const alamat = safeString(alamatRaw);
            const keteranganAlamat = safeString(keteranganAlamatRaw);
            const nomorHP = safeString(nomorHPRaw);
            const namaAyah = safeString(namaAyahRaw);
            const namaIbu = safeString(namaIbuRaw);
            const namaWali = safeString(namaWaliRaw);
            const namaKontakDarurat = safeString(namaKontakDaruratRaw);
            const noHpKontakDarurat = safeString(noHpKontakDaruratRaw);
            const hubunganDenganSiswa = safeString(hubunganDenganSiswaRaw);

            // Validation: Only NIS and Name are required, email is optional
            const validationErrors = [];
            if (nis === null || nis === '' || isNaN(nis)) {
              validationErrors.push('NIS harus berupa angka');
            }
            if (name === '') {
              validationErrors.push('Nama tidak boleh kosong');
            }
            // Email is optional - no validation needed
            
            const isValid = validationErrors.length === 0;

            return {
              row: index + 2, // Excel row number (starting from 2, assuming row 1 is header)
              nis: nis,
              name: name,
              email: email,
              class: kelas,
              angkatan: angkatan,
              absenceNumber: nomorUrut,
              address: alamat,
              addressDesc: keteranganAlamat,
              handphone: nomorHP,
              fatherName: namaAyah,
              motherName: namaIbu,
              maidenName: namaWali,
              emergencyContactName: namaKontakDarurat,
              emergencyContactPhone: noHpKontakDarurat,
              emergencyContactRelationship: hubunganDenganSiswa,
              valid: isValid,
              errors: validationErrors
            };
          }).filter(item => {
            // Filter out completely empty rows
            return item.nis !== null || item.name !== '' || item.email !== '';
          });

          console.log('Raw Excel data:', jsonData);
          console.log('--- DEBUG: Final mapped data before setting state (first 3 rows) ---');
          console.log(JSON.stringify(mappedData.slice(0, 3), null, 2));
          console.log('Mapped data:', mappedData);
          console.log('Valid data count:', mappedData.filter(item => item.valid).length);
          
          setImportData(mappedData);
        } catch (error) {
          console.error('Excel parsing error:', error);
          setNotification({
            show: true,
            message: 'Error membaca file Excel: ' + error.message,
            type: 'error'
          });
        }
      };
      console.log('Starting FileReader...');
      reader.readAsArrayBuffer(file);
    } else {
      console.log('No file selected');
    }
  };

  const handleImportSubmit = async () => {
    console.log('Import submit called. Import data length:', importData.length);
    console.log('Import data:', importData);
    
    if (importData.length === 0) {
      setNotification({
        show: true,
        message: 'Tidak ada data untuk diimpor',
        type: 'error'
      });
      return;
    }

    const validData = importData.filter(item => item.valid);
    console.log('Valid data count:', validData.length);
    console.log('Valid data:', validData);
    
    if (validData.length === 0) {
      setNotification({
        show: true,
        message: 'Tidak ada data valid untuk diimpor',
        type: 'error'
      });
      return;
    }

    setImportLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/students/bulk-import`;
      console.log('API URL:', apiUrl);

      const response = await authFetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          data: validData.map(item => ({
            nis: Number.isInteger(item.nis) ? item.nis : parseInt(item.nis),
            name: item.name,
            email: item.email,
            class: item.class,
            angkatan: item.angkatan,
            absenceNumber: item.absenceNumber,
            address: item.address || '',
            addressDesc: item.addressDesc || '',
            handphone: item.handphone || '',
            fatherName: item.fatherName || '',
            motherName: item.motherName || '',
            maidenName: item.maidenName || '',
            emergencyContactName: item.emergencyContactName || '',
            emergencyContactPhone: item.emergencyContactPhone || '',
            emergencyContactRelationship: item.emergencyContactRelationship || ''
          }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Import gagal');
      }

      const result = await response.json();
      
      // If there are errors, show sample errors for debugging
      let message = result.message;
      if (result.sample_errors && result.sample_errors.length > 0) {
        message += '\n\nSample errors:\n' + result.sample_errors.map(err => 
          `Row ${err.row}: ${err.error} (NIS: ${err.nis}, Name: ${err.name})`
        ).join('\n');
      }
      
      setNotification({
        show: true,
        message: message,
        type: result.summary?.errors > 0 ? 'error' : 'success'
      });

      // Refresh the students list
          const fetchStudents = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
          let apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/students?page=${currentPage}`;
          if (searchQuery && searchQuery.trim() !== '') {
            apiUrl += `&search=${encodeURIComponent(searchQuery)}`;
          }
          const response = await authFetch(apiUrl);
          if (response.ok) {
            const data = await response.json();
            setStudents(data.data);
            setLastPage(data.last_page);
            setTotal(data.total);
          }
        } catch (err) {
          console.error('Error refreshing data:', err);
        }
      };
      
      fetchStudents();
      setShowImportModal(false);
      setImportFile(null);
      setImportData([]);

    } catch (error) {
      setNotification({
        show: true,
        message: 'Error mengimpor data: ' + error.message,
        type: 'error'
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handleGradesFileUpload = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      setImportGradesFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Map Excel columns to our format for grades
          const mappedData = jsonData.map((row, index) => {
            const safeString = (value) => {
              if (value === null || value === undefined) return '';
              return String(value).trim();
            };

            const safeNumber = (value) => {
              if (value === null || value === undefined || value === '') return null;
              const num = parseFloat(value);
              return isNaN(num) ? null : num;
            };

                         // Support multiple column name variations for NIS and average points
             const nisRaw = row['NIS'] || row['nis'] || row['Nis'] || '';
             const averagePointsRaw = row['Rata2 Nilai Akhir'] || row['Nilai Rata-rata'] || row['Nilai Rata Rata'] || row['Average Points'] || 
                                    row['average_points'] || row['Rata-rata'] || row['Rata Rata'] || 
                                    row['Nilai'] || row['Points'] || '';

            const nis = safeNumber(nisRaw);
            const averagePoints = safeNumber(averagePointsRaw);

            // Validation: NIS and average points are required
            const validationErrors = [];
            if (nis === null || nis === '' || isNaN(nis)) {
              validationErrors.push('NIS harus berupa angka');
            }
            if (averagePoints === null || isNaN(averagePoints)) {
              validationErrors.push('Nilai rata-rata harus berupa angka');
            }
            if (averagePoints !== null && (averagePoints < 0 || averagePoints > 100)) {
              validationErrors.push('Nilai rata-rata harus antara 0-100');
            }
            
            const isValid = validationErrors.length === 0;

            return {
              row: index + 2, // Excel row number
              nis: nis,
              averagePoints: averagePoints,
              valid: isValid,
              errors: validationErrors
            };
          }).filter(item => {
            // Filter out completely empty rows
            return item.nis !== null || item.averagePoints !== null;
          });

          setImportGradesData(mappedData);
        } catch (error) {
          setNotification({
            show: true,
            message: 'Error membaca file Excel: ' + error.message,
            type: 'error'
          });
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleGradesImportSubmit = async () => {
    if (importGradesData.length === 0) {
      setNotification({
        show: true,
        message: 'Tidak ada data untuk diimpor',
        type: 'error'
      });
      return;
    }

    const validData = importGradesData.filter(item => item.valid);
    
    if (validData.length === 0) {
      setNotification({
        show: true,
        message: 'Tidak ada data valid untuk diimpor',
        type: 'error'
      });
      return;
    }

    setImportGradesLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8001/';
      const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/students/bulk-update-grades`;

      const response = await authFetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          data: validData.map(item => ({
            nis: Number.isInteger(item.nis) ? item.nis : parseInt(item.nis),
            average_points: item.averagePoints
          }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Import gagal');
      }

      const result = await response.json();
      
      // If there are errors, show sample errors for debugging
      let message = result.message;
      if (result.sample_errors && result.sample_errors.length > 0) {
        message += '\n\nSample errors:\n' + result.sample_errors.map(err => 
          `Row ${err.row}: ${err.error} (NIS: ${err.nis})`
        ).join('\n');
      }
      
      setNotification({
        show: true,
        message: message,
        type: result.summary?.errors > 0 ? 'error' : 'success'
      });

      // Refresh the students list
      const fetchStudents = async () => {
        try {
          const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
          let apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/students?page=${currentPage}`;
          if (searchQuery && searchQuery.trim() !== '') {
            apiUrl += `&search=${encodeURIComponent(searchQuery)}`;
          }
          const response = await authFetch(apiUrl);
          if (response.ok) {
            const data = await response.json();
            setStudents(data.data);
            setLastPage(data.last_page);
            setTotal(data.total);
          }
        } catch (err) {
          console.error('Error refreshing data:', err);
        }
      };
      
      fetchStudents();
      setShowImportGradesModal(false);
      setImportGradesFile(null);
      setImportGradesData([]);

    } catch (error) {
      setNotification({
        show: true,
        message: 'Error mengimpor data: ' + error.message,
        type: 'error'
      });
    } finally {
      setImportGradesLoading(false);
    }
  };

  // Hide notification after 5 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Get user data for sidebar
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);
  
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : ''; 

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  // Function to fetch student statistics
  const fetchStudentStats = async (studentId) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const apiPrefix = 'admin'; // Always use admin for this functionality
      
      // Fetch all statistics data in parallel for the specific student
      const [
        kersosResponse,
        lemburResponse,
        apresiasiResponse,
        ketidakhadiranResponse,
        spResponse,
        poinSikapResponse
      ] = await Promise.all([
        authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/jam-kersos`),
        authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/jam-lembur`),
        authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/poin-apresiasi`),
        authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/ketidakhadiran`),
        authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/surat-peringatan`),
        authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/poin-sikap`)
      ]);

      const stats = {
        total_jam_kersos: 0,
        total_jam_lembur: 0,
        total_jam_lembur_debt_only: 0,
        total_poin_apresiasi: 0,
        ketidakhadiran: { sakit: 0, ijin: 0, alpha: 0 },
        total_poin_sikap: 0,
        surat_peringatan: [],
        sisa_kompen_murni: 0,
        sisa_kompen_alat: 0,
        total_jam_ditebus: 0,
        sisa_total_kompen: 0
      };

      // Process kersos data
      if (kersosResponse.ok) {
        const kersosData = await kersosResponse.json();
        const kersosRecords = kersosData.data || [];
        stats.total_jam_kersos = kersosRecords.reduce((sum, record) => {
          const hour = parseFloat(record.hour) || 0;
          return sum + hour;
        }, 0);
      }

      // Process lembur data
      if (lemburResponse.ok) {
        const lemburData = await lemburResponse.json();
        const lemburRecords = lemburData.data || [];
        
        // Calculate total jam lembur (all records - net balance)
        stats.total_jam_lembur = lemburRecords.reduce((sum, record) => {
          const hour = parseFloat(record.hour) || 0;
          return sum + hour;
        }, 0);
        
        // Calculate debt only (negative hours only)
        stats.total_jam_lembur_debt_only = lemburRecords.reduce((sum, record) => {
          const hour = parseFloat(record.hour) || 0;
          if (hour < 0) {
            return sum + hour;
          }
          return sum;
        }, 0);
        
        // Get kompen stats if available
        if (lemburData.kompen_stats) {
          stats.sisa_kompen_murni = lemburData.kompen_stats.sisa_kompen_murni || 0;
          stats.sisa_kompen_alat = lemburData.kompen_stats.sisa_kompen_alat || 0;
          stats.total_jam_ditebus = lemburData.kompen_stats.total_redeemed || 0;
        }
        
        // Calculate sisa_total_kompen as: total_jam_lembur_debt_only + total_jam_ditebus
        // This ensures the formula: Sisa Total Kompen = Total Jam Lembur + Total Jam Lembur Ditebus
        stats.sisa_total_kompen = stats.total_jam_lembur_debt_only + stats.total_jam_ditebus;
      }

      // Process apresiasi data
      if (apresiasiResponse.ok) {
        const apresiasiData = await apresiasiResponse.json();
        const apresiasiRecords = apresiasiData.data || [];
        stats.total_poin_apresiasi = apresiasiRecords.reduce((sum, record) => {
          const point = parseFloat(record.point) || 0;
          return sum + point;
        }, 0);
      }

      // Process ketidakhadiran data
      if (ketidakhadiranResponse.ok) {
        const ketidakhadiranData = await ketidakhadiranResponse.json();
        const ketidakhadiranRecords = ketidakhadiranData.data || [];
        stats.ketidakhadiran = ketidakhadiranRecords.reduce((acc, record) => {
          const type = record.type?.toLowerCase();
          if (type === 'sakit') acc.sakit += record.days || 1;
          else if (type === 'ijin') acc.ijin += record.days || 1;
          else if (type === 'alfa' || type === 'alpha') acc.alpha += record.days || 1;
          return acc;
        }, { sakit: 0, ijin: 0, alpha: 0 });
      }

      // Process surat peringatan data
      if (spResponse.ok) {
        const spData = await spResponse.json();
        const spRecords = spData.data || [];
        // Filter only approved SPs with status 'terima'
        const approvedSP = spRecords.filter(sp => sp.status === 'terima');
        stats.surat_peringatan = approvedSP.map(sp => sp.type).filter(Boolean);
      }

      // Process poin sikap data
      if (poinSikapResponse.ok) {
        const poinSikapData = await poinSikapResponse.json();
        if (poinSikapData.success) {
          stats.total_poin_sikap = poinSikapData.data.total_poin_sikap || 0;
        }
      }

      return stats;
    } catch (error) {
      console.error('Failed to fetch student statistics:', error);
      return {
        total_jam_kersos: 0,
        total_jam_lembur: 0,
        total_jam_lembur_debt_only: 0,
        total_poin_apresiasi: 0,
        ketidakhadiran: { sakit: 0, ijin: 0, alpha: 0 },
        total_poin_sikap: 0,
        surat_peringatan: [],
        sisa_kompen_murni: 0,
        sisa_kompen_alat: 0,
        total_jam_ditebus: 0,
        sisa_total_kompen: 0
      };
    }
  };

  // Function to handle download status siswa
  const handleDownloadStatusSiswa = async () => {
    try {
      setNotification({
        show: true,
        message: 'Mohon menunggu. Jangan menutup browser. Sedang memproses data siswa...',
        type: 'info'
      });

      // Fetch all students (without pagination limits)
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8001/';
      let allStudents = [];
      let currentPage = 1;
      let hasMorePages = true;

      // Fetch all pages of students
      while (hasMorePages) {
        const studentsUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/students?page=${currentPage}&per_page=100`;
        const studentsResponse = await authFetch(studentsUrl);
        
        if (!studentsResponse.ok) {
          throw new Error('Gagal mengambil data siswa');
        }

        const studentsData = await studentsResponse.json();
        const pageStudents = studentsData.data || [];
        
        allStudents = [...allStudents, ...pageStudents];
        
        // Check if there are more pages
        hasMorePages = studentsData.current_page < studentsData.last_page;
        currentPage++;
        
        // Update progress notification
         setNotification({
           show: true,
           message: `Mohon menunggu. Jangan menutup browser. Mengambil data siswa... (${allStudents.length} siswa)`,
           type: 'info'
         });
      }

      // Fetch statistics for each student with throttling to prevent resource exhaustion
      const studentsWithStats = [];
      const batchSize = 10; // Process 10 students at a time
      const delayBetweenBatches = 500; // 500ms delay between batches
      
      for (let i = 0; i < allStudents.length; i += batchSize) {
        const batch = allStudents.slice(i, i + batchSize);
        
        // Update progress notification
        setNotification({
          show: true,
          message: `Mohon menunggu. Jangan menutup browser. Memproses statistik siswa... (${i + 1}-${Math.min(i + batchSize, allStudents.length)} dari ${allStudents.length})`,
          type: 'info'
        });
        
        // Process current batch
        const batchResults = await Promise.all(
          batch.map(async (student) => {
            const stats = await fetchStudentStats(student.id);
            return {
              ...student,
              stats
            };
          })
        );
        
        studentsWithStats.push(...batchResults);
        
        // Add delay between batches (except for the last batch)
        if (i + batchSize < allStudents.length) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
        }
      }

      // Sort students by NIS in ascending order
      const sortedStudents = studentsWithStats.sort((a, b) => {
        const nisA = parseInt(a.nis) || 0;
        const nisB = parseInt(b.nis) || 0;
        return nisA - nisB;
      });

      // Prepare Excel data
      const excelData = sortedStudents.map(student => {
        // Helper function to clean up floating point errors
        const cleanFloatValue = (value) => {
          const num = value ?? 0;
          // If the absolute value is less than 0.0001, treat it as 0
          return Math.abs(num) < 0.0001 ? 0 : num;
        };

        return {
          'Kelas': student.class || '',
          'NIS': student.nis || '',
          'Nama Siswa': student.name || '',
          'Rata-rata Nilai': cleanFloatValue(student.average_points),
          'Total Jam Kersos': cleanFloatValue(student.stats.total_jam_kersos) > 0 ? 0 : cleanFloatValue(student.stats.total_jam_kersos),
          'Total Jam Lembur': cleanFloatValue(student.stats.total_jam_lembur_debt_only),
          'Total Jam Lembur Ditebus': cleanFloatValue(student.stats.total_jam_ditebus),
          'Sisa Total Kompen': cleanFloatValue(student.stats.sisa_total_kompen),
          'Sisa Kompen Murni': cleanFloatValue(student.stats.sisa_kompen_murni),
          'Sisa Kompen Alat': cleanFloatValue(student.stats.sisa_kompen_alat),
          'Total Poin Apresiasi': cleanFloatValue(student.stats.total_poin_apresiasi),
          'Ketidakhadiran Sakit': student.stats.ketidakhadiran?.sakit ?? 0,
          'Ketidakhadiran Ijin': student.stats.ketidakhadiran?.ijin ?? 0,
          'Ketidakhadiran Alfa': student.stats.ketidakhadiran?.alpha ?? 0,
          'Poin Sikap': (parseFloat(student.stats.total_poin_sikap) || 0) + 100,
          'SP': student.stats.surat_peringatan?.length > 0 ? student.stats.surat_peringatan.join(',') : ''
        };
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Get current date and time
      const now = new Date();
      const dateStr = now.toLocaleDateString('id-ID', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
      const timeStr = now.toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      const titleText = `Status Murid tanggal ${dateStr} ${timeStr}`;

      // Add title row
      XLSX.utils.sheet_add_aoa(worksheet, [[titleText]], { origin: 'A1' });
      
      // Merge title cells
      if (!worksheet['!merges']) worksheet['!merges'] = [];
      worksheet['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 15 } });
      
      // Shift data down by 2 rows to accommodate title
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      const newData = [[''], [''], ...excelData.map(row => Object.values(row))];
      const headers = Object.keys(excelData[0] || {});
      newData[1] = headers;
      
      // Recreate worksheet with title and data
      const finalWorksheet = XLSX.utils.aoa_to_sheet([
        [titleText],
        [],
        headers,
        ...excelData.map(row => Object.values(row))
      ]);
      
      // Merge title cells
      if (!finalWorksheet['!merges']) finalWorksheet['!merges'] = [];
      finalWorksheet['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 15 } });

      // Set column widths to ensure content is visible
      const columnWidths = [
        { wch: 10 }, // Kelas
        { wch: 12 }, // NIS
        { wch: 25 }, // Nama Siswa
        { wch: 15 }, // Rata-rata Nilai
        { wch: 15 }, // Total Jam Kersos
        { wch: 18 }, // Total Jam Lembur (debt only)
        { wch: 20 }, // Total Jam Lembur Ditebus
        { wch: 18 }, // Sisa Total Kompen
        { wch: 18 }, // Sisa Kompen Murni
        { wch: 18 }, // Sisa Kompen Alat
        { wch: 18 }, // Total Poin Apresiasi
        { wch: 18 }, // Ketidakhadiran Sakit
        { wch: 18 }, // Ketidakhadiran Ijin
        { wch: 18 }, // Ketidakhadiran Alfa
        { wch: 15 }, // Poin Sikap
        { wch: 15 }  // SP
      ];
      finalWorksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, finalWorksheet, 'Status Siswa');

      // Generate and download file
      const fileName = `Status_Murid_${now.toLocaleDateString('id-ID').replace(/\//g, '_')}_${timeStr.replace(/:/g, '-')}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      setNotification({
        show: true,
        message: `File ${fileName} berhasil diunduh!`,
        type: 'success'
      });

    } catch (error) {
      console.error('Error downloading status siswa:', error);
      setNotification({
        show: true,
        message: 'Gagal mengunduh data: ' + error.message,
        type: 'error'
      });
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <h1>Manajemen Siswa</h1>
          <p>Kelola data siswa dengan mudah dan efisien</p>
        </div>
      </PageHeader>
      
      <DashboardGrid>
        <MainSection>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Cari siswa berdasarkan nama, NIS, atau kelas"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchContainer>
          <ActionHeader>
            <ActionButtonGroup>
              <ImportButton onClick={() => {
                setImportFile(null);
                setImportData([]);
                setShowImportModal(true);
              }}>
                📥 Import Excel
              </ImportButton>
              <ImportButton onClick={() => {
                setImportGradesFile(null);
                setImportGradesData([]);
                setShowImportGradesModal(true);
              }} style={{ backgroundColor: '#9b59b6', marginRight: '1rem' }}>
                📊 Import Nilai
              </ImportButton>
              <ImportButton onClick={handleDownloadStatusSiswa} style={{ backgroundColor: '#e67e22', marginRight: '1rem' }}>
                📊 Download Status Siswa
              </ImportButton>
              <AddButton onClick={() => {
                setFormData({
                  nis: '',
                  name: '',
                  email: '',
                  class: '',
                  angkatan: '',
                  noUrut: '',
                  averagePoints: '',
                  address: '',
                  addressNote: '',
                  phone: '',
                  fatherName: '',
                  motherName: '',
                  maidenName: '',
                  emergencyName: '',
                  emergencyPhone: '',
                  emergencyRelation: '',
                  avatarUrl: '',
                  avatarFile: null
                });
                setShowAddModal(true);
              }}>
                + Tambah Siswa
              </AddButton>
            </ActionButtonGroup>
          </ActionHeader>
          
          <TableContainer>
            <Table>
          <thead>
            <tr>
              <th>Foto</th>
              <th>NIS</th>
              <th>Nama</th>
              <th>Kelas</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5}>Memuat data siswa...</td></tr>
            ) : students.length === 0 ? (
              <tr><td colSpan={5}>Tidak ada data siswa.</td></tr>
            ) : (
              students.map(student => (
                <tr key={student.id}>
                  <td>
                    {student.avatarUrl ? (
                      <img 
                        src={student.avatarUrl.startsWith('http') ? student.avatarUrl : `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8001/'}${student.avatarUrl}`} 
                        alt={student.name} 
                        style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.src = '/images/user.png';
                        }}
                      />
                    ) : (
                      <img 
                        src="/images/user.png" 
                        alt={student.name} 
                        style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
                      />
                    )}
                  </td>
                  <td>{student.nis}</td>
                  <td>{student.name}</td>
                  <td>{student.class}</td>
                  <td>
                    <Button onClick={() => { setSelectedStudent(student); setShowViewModal(true); }} style={{ backgroundColor: '#2b5da1', marginRight: '0.5rem', padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Lihat">
                      <ViewIcon />
                    </Button>
                    <Button onClick={() => openEditModal(student)} style={{ backgroundColor: '#2b5da1', marginRight: '0.5rem', padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Edit">
                      <EditIcon />
                    </Button>
                    <Button onClick={() => navigate('/admin/status-siswa', { state: { student: student } })} style={{ backgroundColor: '#27ae60', marginRight: '0.5rem', padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Status">
                      <StatusIcon />
                    </Button>
                    <DeleteButton onClick={() => openDeleteModal(student)} style={{ padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Hapus">
                      <DeleteIcon />
                    </DeleteButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
            </Table>
          </TableContainer>
          {/* Pagination Controls */}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 8 }}>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} aria-label="Sebelumnya">&lt;</button>
            <span>Halaman {currentPage} dari {lastPage}</span>
            <button onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))} disabled={currentPage === lastPage} aria-label="Berikutnya">&gt;</button>
          </div>
        </MainSection>
        
        <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} />
      </DashboardGrid>

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast $type={notification.type}>
          <NotificationMessage>{notification.message}</NotificationMessage>
          <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>×</CloseNotificationButton>
        </NotificationToast>
      )}

      {/* View Modal */}
      {showViewModal && selectedStudent && (
        <Modal onClick={() => setShowViewModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h2>Detail Siswa</h2>
            <FormGroup>
              <Label>NIS</Label>
              <div>{selectedStudent.nis}</div>
            </FormGroup>
            <FormGroup>
              <Label>Nama</Label>
              <div>{selectedStudent.name}</div>
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <div>{selectedStudent.email}</div>
            </FormGroup>
            <FormGroup>
              <Label>Kelas</Label>
              <div>{selectedStudent.class}</div>
            </FormGroup>
            <FormGroup>
              <Label>Angkatan</Label>
              <div>{selectedStudent.angkatan}</div>
            </FormGroup>
            <FormGroup>
              <Label>No. Urut (Absensi)</Label>
              <div>{selectedStudent.absenceNumber}</div>
            </FormGroup>
            <FormGroup>
              <Label>Rata-rata Nilai Semester</Label>
              <div>{selectedStudent.average_points || '0.00'}</div>
            </FormGroup>
            <FormGroup>
              <Label>Alamat</Label>
              <div>{selectedStudent.address}</div>
            </FormGroup>
            <FormGroup>
              <Label>Keterangan Alamat</Label>
              <div>{selectedStudent.addressDesc}</div>
            </FormGroup>
            <FormGroup>
              <Label>No. HP</Label>
              <div>{selectedStudent.handphone}</div>
            </FormGroup>
            <FormGroup>
              <Label>Nama Ayah</Label>
              <div>{selectedStudent.fatherName}</div>
            </FormGroup>
            <FormGroup>
              <Label>Nama Ibu</Label>
              <div>{selectedStudent.motherName}</div>
            </FormGroup>
            <FormGroup>
              <Label>Nama Gadis Ibu</Label>
              <div>{selectedStudent.maidenName}</div>
            </FormGroup>
            <FormGroup>
              <Label>Nama Kontak Darurat</Label>
              <div>{selectedStudent.emergencyContactName}</div>
            </FormGroup>
            <FormGroup>
              <Label>No. HP Darurat</Label>
              <div>{selectedStudent.emergencyContactPhone}</div>
            </FormGroup>
            <FormGroup>
              <Label>Hubungan Darurat</Label>
              <div>{selectedStudent.emergencyContactRelationship}</div>
            </FormGroup>
            <FormGroup>
              <Label>Foto</Label>
              {selectedStudent.avatarUrl ? (
                <img 
                  src={selectedStudent.avatarUrl.startsWith('http') ? selectedStudent.avatarUrl : `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8001/'}${selectedStudent.avatarUrl}`} 
                  alt="Foto Siswa" 
                  style={{ maxWidth: 120, borderRadius: 8 }}
                  onError={(e) => {
                    e.target.src = '/images/user.png';
                  }}
                />
              ) : (
                <img 
                  src="/images/user.png" 
                  alt="Foto Siswa" 
                  style={{ maxWidth: 120, borderRadius: 8 }}
                />
              )}
            </FormGroup>
            <ButtonGroup>
              <CancelButton type="button" onClick={() => setShowViewModal(false)}>
                Tutup
              </CancelButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Add Student Modal */}
      {showAddModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Tambah Siswa Baru</h2>
              <CloseButton onClick={() => setShowAddModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <form onSubmit={handleAddStudent}>
              {/* Informasi Pribadi */}
              <h3 style={{ marginTop: 0 }}>Informasi Pribadi</h3>
              <br />
              <FormGroup>
                <Label>Email (Opsional)</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Masukkan email siswa (opsional)"
                />
              </FormGroup>

              <FormGroup>
                <Label>NIS</Label>
                <Input
                  type="number"
                  name="nis"
                  value={formData.nis}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Nama Lengkap</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Kelas</Label>
                <Input
                  type="text"
                  name="class"
                  value={formData.class}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Angkatan</Label>
                <Input
                  type="text"
                  name="angkatan"
                  value={formData.angkatan}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>No Urut</Label>
                <Input
                  type="number"
                  name="noUrut"
                  value={formData.noUrut}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Rata-rata Nilai Semester</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="999.99"
                  name="averagePoints"
                  value={formData.averagePoints}
                  onChange={handleInputChange}
                  placeholder="0.00"
                />
              </FormGroup>
              <FormGroup>
                <Label>Alamat Domisili</Label>
                <Textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Keterangan Domisili</Label>
                <Input
                  type="text"
                  name="addressNote"
                  value={formData.addressNote}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Nomor HP</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              {/* Upload Avatar */}
              <FormGroup>
                <Label>Upload Foto/Avatar</Label>
                <Input
                  name="avatar"
                  type="file"
                  accept="image/*"
                  onChange={e => {
                    const file = e.target.files[0];
                    setFormData(prev => ({ ...prev, avatarFile: file }));
                  }}
                />
                {formData.avatarFile ? (
                  <div style={{ marginTop: 8 }}>
                    <img
                      src={URL.createObjectURL(formData.avatarFile)}
                      alt="Avatar Preview"
                      style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  </div>
                ) : formData.avatarUrl ? (
                  <div style={{ marginTop: 8 }}>
                    <img
                      src={formData.avatarUrl.startsWith('http') ? formData.avatarUrl : `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/'}${formData.avatarUrl}`}
                      alt="Avatar Preview"
                      style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  </div>
                ) : null}
              </FormGroup>

              {/* Informasi Keluarga */}
              <h3 style={{ marginTop: 24 }}>Informasi Keluarga</h3>
              <br />
              <FormGroup>
                <Label>Nama Ayah</Label>
                <Input
                  type="text"
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Nama Ibu</Label>
                <Input
                  type="text"
                  name="motherName"
                  value={formData.motherName}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Nama Wali</Label>
                <Input
                  type="text"
                  name="maidenName"
                  value={formData.maidenName}
                  onChange={handleInputChange}
                />
              </FormGroup>

              {/* Kontak Darurat */}
              <h3 style={{ marginTop: 24 }}>Kontak Darurat</h3>
              <br />
              <FormGroup>
                <Label>Nama</Label>
                <Input
                  type="text"
                  name="emergencyName"
                  value={formData.emergencyName}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Nomor Telepon / HP</Label>
                <Input
                  type="tel"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Hubungan dengan siswa</Label>
                <Input
                  type="text"
                  name="emergencyRelation"
                  value={formData.emergencyRelation}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <ButtonGroup>
                <CancelButton type="button" onClick={() => setShowAddModal(false)}>
                  Batal
                </CancelButton>
                <SubmitButton type="submit">
                  Simpan
                </SubmitButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* Edit Student Modal */}
      {showEditModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Edit Data Siswa</h2>
              <CloseButton onClick={() => setShowEditModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <form onSubmit={handleEditStudent}>
              {/* Informasi Pribadi */}
              <h3 style={{ marginTop: 0 }}>Informasi Pribadi</h3>
              <FormGroup>
                <Label>Email (Opsional)</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Masukkan email siswa (opsional)"
                />
              </FormGroup>
              <FormGroup>
                <Label>NIS</Label>
                <Input
                  type="number"
                  name="nis"
                  value={formData.nis}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Nama Lengkap</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Kelas</Label>
                <Input
                  type="text"
                  name="class"
                  value={formData.class}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Angkatan</Label>
                <Input
                  type="text"
                  name="angkatan"
                  value={formData.angkatan}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>No Urut</Label>
                <Input
                  type="number"
                  name="noUrut"
                  value={formData.noUrut}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Rata-rata Nilai Semester</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="999.99"
                  name="averagePoints"
                  value={formData.averagePoints}
                  onChange={handleInputChange}
                  placeholder="0.00"
                />
              </FormGroup>
              <FormGroup>
                <Label>Alamat Domisili</Label>
                <Textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Keterangan Domisili</Label>
                <Input
                  type="text"
                  name="addressNote"
                  value={formData.addressNote}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Nomor HP</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              {/* Upload Avatar */}
              <FormGroup>
                <Label>Upload Foto/Avatar</Label>
                <Input
                  name="avatar"
                  type="file"
                  accept="image/*"
                  onChange={e => {
                    const file = e.target.files[0];
                    setFormData(prev => ({ ...prev, avatarFile: file }));
                  }}
                />
                {formData.avatarFile ? (
                  <div style={{ marginTop: 8 }}>
                    <img
                      src={URL.createObjectURL(formData.avatarFile)}
                      alt="Avatar Preview"
                      style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  </div>
                ) : formData.avatarUrl ? (
                  <div style={{ marginTop: 8 }}>
                    <img
                      src={formData.avatarUrl.startsWith('http') ? formData.avatarUrl : `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/'}${formData.avatarUrl}`}
                      alt="Avatar Preview"
                      style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  </div>
                ) : null}
              </FormGroup>

              {/* Informasi Keluarga */}
              <h3 style={{ marginTop: 24 }}>Informasi Keluarga</h3>
              <FormGroup>
                <Label>Nama Ayah</Label>
                <Input
                  type="text"
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Nama Ibu</Label>
                <Input
                  type="text"
                  name="motherName"
                  value={formData.motherName}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Nama Wali</Label>
                <Input
                  type="text"
                  name="maidenName"
                  value={formData.maidenName}
                  onChange={handleInputChange}
                />
              </FormGroup>
              {/* Kontak Darurat */}
              <h3 style={{ marginTop: 24 }}>Kontak Darurat</h3>
              <FormGroup>
                <Label>Nama</Label>
                <Input
                  type="text"
                  name="emergencyName"
                  value={formData.emergencyName}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Nomor Telepon / HP</Label>
                <Input
                  type="tel"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Hubungan dengan siswa</Label>
                <Input
                  type="text"
                  name="emergencyRelation"
                  value={formData.emergencyRelation}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <ButtonGroup>
                <CancelButton type="button" onClick={() => setShowEditModal(false)}>
                  Batal
                </CancelButton>
                <SubmitButton type="submit">
                  Simpan Perubahan
                </SubmitButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Modal>
          <ModalContent>
            <ModalHeader style={{ background: '#ffeaea', borderRadius: '0.5rem 0.5rem 0 0', borderBottom: '2px solid #e74c3c', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '2rem', color: '#e74c3c', marginRight: 12 }}>⚠️</span>
              <h2 style={{ color: '#e74c3c', margin: 0, fontWeight: 700, fontSize: '1.3rem' }}>Hapus Siswa</h2>
              <CloseButton onClick={() => setShowDeleteModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <p style={{ color: '#b71c1c', fontWeight: 500, marginTop: 20 }}>
              Apakah Anda yakin ingin menghapus data siswa{' '}
              <strong>{selectedStudent?.name}</strong>?
            </p>
            <p style={{ color: '#b71c1c', fontSize: '0.95rem', marginBottom: 32 }}>Tindakan ini <strong>TIDAK DAPAT</strong> dibatalkan.</p>
            <ButtonGroup>
              <CancelButton onClick={() => setShowDeleteModal(false)}>
                Batal
              </CancelButton>
              <DeleteButton onClick={handleDeleteStudent}>
                Hapus
              </DeleteButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Import Excel Modal */}
      {showImportModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Import Data Siswa dari Excel</h2>
              <CloseButton onClick={() => setShowImportModal(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>Format Excel yang diperlukan:</Label>
              <ImportFormatInfo>
                <p><strong>Kolom yang WAJIB ada:</strong></p>
                <ul style={{ color: 'red', fontWeight: 'bold' }}>
                  <li><strong>NIS</strong> - Nomor Induk Siswa (harus berupa angka)</li>
                  <li><strong>Nama</strong> - Nama lengkap siswa (tidak boleh kosong)</li>
                </ul>
                <p><strong>Kolom opsional:</strong></p>
                <ul style={{ color: 'gray' }}>
                  <li><strong>Email</strong> - Email siswa (boleh kosong)</li>
                  <li><strong>Kelas</strong> - Kelas siswa</li>
                  <li><strong>Angkatan</strong> - Angkatan siswa</li>
                  <li><strong>Nomor Urut</strong> - Nomor urut absensi (angka)</li>
                  <li><strong>Alamat Domisili</strong> - Alamat tempat tinggal</li>
                  <li><strong>Keterangan Domisili</strong> - Keterangan alamat</li>
                  <li><strong>Nomor HP</strong> - Nomor handphone</li>
                  <li><strong>Nama Ayah</strong> - Nama ayah siswa</li>
                  <li><strong>Nama Ibu</strong> - Nama ibu siswa</li>
                  <li><strong>Nama Wali</strong> - Nama wali siswa</li>
                  <li><strong>Nama Kontak Darurat</strong> - Nama kontak darurat</li>
                  <li><strong>No Hp Kontak Darurat</strong> - Nomor HP kontak darurat</li>
                  <li><strong>Hubungan dengan siswa</strong> - Hubungan kontak darurat dengan siswa</li>
                </ul>
                <p><strong>Catatan:</strong> Jika NIS sudah ada di database, data akan diperbarui. Kolom opsional boleh kosong.</p>
              </ImportFormatInfo>
            </FormGroup>

            <FormGroup>
              <Label>Pilih File Excel (.xlsx, .xls)</Label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ccc',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  backgroundColor: '#f5f6fa'
                }}
              />
              <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'gray' }}>
                Debug: File input should trigger alert when file is selected
              </div>
            </FormGroup>

            {importData.length > 0 && (
              <FormGroup>
                <Label>Preview Data ({importData.length} baris)</Label>
                <PreviewTable>
                  <thead>
                    <tr>
                      <th>Baris</th>
                      <th>NIS</th>
                      <th>Nama</th>
                      <th>Email</th>
                      <th>Kelas</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importData.slice(0, 10).map((item, index) => (
                      <tr key={index} style={{ backgroundColor: item.valid ? '#f8fff8' : '#fff8f8' }}>
                        <td>{item.row}</td>
                        <td>{item.nis}</td>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>{item.class}</td>
                        <td>
                          <StatusBadge $valid={item.valid}>
                            {item.valid ? '✓ Valid' : '✗ Error'}
                          </StatusBadge>
                          {!item.valid && item.errors && (
                            <div style={{ fontSize: '0.75rem', color: 'red', marginTop: '0.25rem' }}>
                              {item.errors.join(', ')}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </PreviewTable>
                {importData.length > 10 && (
                  <p style={{ textAlign: 'center', marginTop: '0.5rem', color: 'var(--text-light)' }}>
                    ... dan {importData.length - 10} baris lainnya
                  </p>
                )}
              </FormGroup>
            )}

            <ButtonGroup>
              <CancelButton onClick={() => setShowImportModal(false)}>
                Batal
              </CancelButton>
              <SubmitButton 
                onClick={handleImportSubmit} 
                disabled={importLoading || importData.length === 0}
              >
                {importLoading ? 'Mengimpor...' : `Import ${importData.filter(item => item.valid).length} Data`}
              </SubmitButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Import Grades Modal */}
      {showImportGradesModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Import Nilai Rata-rata Siswa dari Excel</h2>
              <CloseButton onClick={() => setShowImportGradesModal(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>Format Excel yang diperlukan:</Label>
              <ImportFormatInfo>
                                 <p><strong>Kolom yang WAJIB ada:</strong></p>
                 <ul style={{ color: 'red', fontWeight: 'bold' }}>
                   <li><strong>NIS</strong> - Nomor Induk Siswa (harus berupa angka)</li>
                   <li><strong>Rata2 Nilai Akhir</strong> - Nilai rata-rata semester (0-100)</li>
                 </ul>
                 <p><strong>Variasi nama kolom yang didukung:</strong></p>
                 <ul style={{ color: 'gray' }}>
                   <li><strong>NIS:</strong> "NIS", "nis", "Nis"</li>
                   <li><strong>Nilai:</strong> "Rata2 Nilai Akhir", "Nilai Rata-rata", "Nilai Rata Rata", "Average Points", "average_points", "Rata-rata", "Rata Rata", "Nilai", "Points"</li>
                 </ul>
                <p><strong>Catatan:</strong> Sistem akan mencari NIS yang sesuai di database dan memperbarui nilai rata-rata siswa tersebut.</p>
              </ImportFormatInfo>
            </FormGroup>

            <FormGroup>
              <Label>Pilih File Excel (.xlsx, .xls)</Label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleGradesFileUpload}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ccc',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  backgroundColor: '#f5f6fa'
                }}
              />
            </FormGroup>

            {importGradesData.length > 0 && (
              <FormGroup>
                <Label>Preview Data ({importGradesData.length} baris)</Label>
                <PreviewTable>
                  <thead>
                    <tr>
                      <th>Baris</th>
                      <th>NIS</th>
                      <th>Nilai Rata-rata</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importGradesData.slice(0, 10).map((item, index) => (
                      <tr key={index} style={{ backgroundColor: item.valid ? '#f8fff8' : '#fff8f8' }}>
                        <td>{item.row}</td>
                        <td>{item.nis}</td>
                        <td>{item.averagePoints}</td>
                        <td>
                          <StatusBadge $valid={item.valid}>
                            {item.valid ? '✓ Valid' : '✗ Error'}
                          </StatusBadge>
                          {!item.valid && item.errors && (
                            <div style={{ fontSize: '0.75rem', color: 'red', marginTop: '0.25rem' }}>
                              {item.errors.join(', ')}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </PreviewTable>
                {importGradesData.length > 10 && (
                  <p style={{ textAlign: 'center', marginTop: '0.5rem', color: 'var(--text-light)' }}>
                    ... dan {importGradesData.length - 10} baris lainnya
                  </p>
                )}
              </FormGroup>
            )}

            <ButtonGroup>
              <CancelButton onClick={() => setShowImportGradesModal(false)}>
                Batal
              </CancelButton>
              <SubmitButton 
                onClick={handleGradesImportSubmit} 
                disabled={importGradesLoading || importGradesData.length === 0}
              >
                {importGradesLoading ? 'Mengimpor...' : `Update ${importGradesData.filter(item => item.valid).length} Nilai`}
              </SubmitButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-light);
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  order: 2;
  @media (max-width: 1100px) {
    order: 1;
  }
`;

const ActionHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
`;

const SearchContainer = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const TableContainer = styled.div`
  background-color: var(--background);
  border-radius: 0.5rem;
  box-shadow: var(--box-shadow);
  overflow: hidden;
  margin-bottom: 1rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--border);
  }

  th {
    background-color: var(--background-light);
    font-weight: 600;
  }

  tbody tr:hover {
    background-color: var(--background-light);
  }
`;

const ActionButton = styled.button`
  background-color: ${props => props.$danger ? 'var(--error)' : 'var(--primary)'};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  margin-right: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const ActionButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const AddButton = styled.button`
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: #219150;
    opacity: 0.95;
  }
`;

const ImportButton = styled.button`
  background-color: #3498db;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
  margin-right: 1rem;

  &:hover {
    background-color: #2980b9;
    opacity: 0.95;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--background);
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  
  &:hover {
    color: var(--text-dark);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SubmitButton = styled(Button)`
  background-color: var(--primary);
  color: white;
  border: none;
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  border: 1px solid var(--border);
  color: var(--text);
`;

const DeleteButton = styled.button`
  background-color: #e74c3c !important;
  color: #fff !important;
  font-weight: bold;
  border: 2px solid #c0392b;
  box-shadow: 0 2px 8px rgba(231, 76, 60, 0.15);
  letter-spacing: 0.5px;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  margin-right: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: opacity 0.2s, background-color 0.2s;
  &:hover, &:focus {
    background-color: #c0392b !important;
    outline: 2px solid #e74c3c;
    color: #fff !important;
    opacity: 0.95;
  }
`;


// Notification Toast Styles
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;

const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
`;

const ImportFormatInfo = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 0.5rem;
  
  p {
    margin: 0 0 0.5rem 0;
  }
  
  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }
  
  li {
    margin-bottom: 0.25rem;
  }
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  
  th, td {
    padding: 0.5rem;
    text-align: left;
    border: 1px solid #dee2e6;
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
  }
  
  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => props.$valid ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$valid ? '#155724' : '#721c24'};
`;

export default ManajemenSiswaPage;